import { FC, ReactNode, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Preloader } from 'shared/ui/spinner';
import { Flex } from 'shared/ui/flex';
import { CustomError } from 'shared/api';
import {
	ButtonContained,
	ButtonContainedProps,
} from '../buttons/buttonContained';
import { showError } from 'shared/lib/asyncActionFeedback';
import { isRejectedWithValue, PayloadAction } from '@reduxjs/toolkit';

interface WithFeedbackProps extends ButtonContainedProps {
	onClick: () => Promise<PayloadAction<unknown> | void>;
	children: ReactNode;
	disableSuccessToast?: boolean;
	disableErrorToast?: boolean;
	successToastLabel?: string;
	onSuccess?: () => void;
	disablePreloader?: boolean;
	onError?: (error: CustomError) => void;
}

export const withAsyncActionFeedback = <P extends WithFeedbackProps>(
	WrappedComponent: FC<ButtonContainedProps> = ButtonContained,
	disableSuccessToast = false,
	onlyPreloader = false,
) => {
	return (props: Omit<P, keyof WithFeedbackProps> & WithFeedbackProps) => {
		const [showPreloader, setShowPreloader] = useState(onlyPreloader);
		const {
			onClick,
			onSuccess,
			disablePreloader,
			children,
			successToastLabel = 'Данные успешно отправлены',
			onError,
			disableErrorToast = false,
			...rest
		} = props as P;

		const submitAction = async () => {
			if (onClick) {
				setShowPreloader(true);
				const res = await onClick();


				setShowPreloader(false);

				if (res) {
					if (isRejectedWithValue(res)) {
						onError?.(res.payload as CustomError);
						if (!disableErrorToast) {
							showError(res.payload as CustomError);
						}
					} else {
						onSuccess?.();
						if (!disableSuccessToast) {
							toast.success(successToastLabel);
						}
					}
				}
			}
		};

		useEffect(() => {
			if (disablePreloader) {
				setShowPreloader(false);
			}
		}, [disablePreloader]);

		return (
			<>
				{showPreloader ? (
					<WrappedComponent isFullWidth={true} {...(rest as P)}>
						<Flex $justify="center">
							<Preloader
								$borderColor={'#0c21634d'}
								$borderTopColor={'#7694f3'}
								$width="15px"
								$height="15px"
							/>
						</Flex>
					</WrappedComponent>
				) : (
					<WrappedComponent
						{...(rest as P)}
						onClick={onlyPreloader ? onClick : submitAction}
					>
						{children}
					</WrappedComponent>
				)}
			</>
		);
	};
};
